import { useMemo } from 'react'
import { HOTEL_OFFERS_LIMIT } from 'constants/charterFlight'
import type { FlightsClassTypes } from 'constants/flight'
import { useMaldivesDirectLightOfferList } from './useMaldivesDirectLightOfferList'
import { useMaldivesOffersPricing, type MaldivesCabinHotelEntry } from './useMaldivesOffersPricing'
import type { DateRange, CalendarItemBundleData } from './types'

interface UseMaldivesDirectCalendarPricingParams {
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  selectedFareClass?: FlightsClassTypes
  nights: number
  charterBundleRoute?: App.CharterBundleRoute
  dateRangesByMonth: Record<string, Array<DateRange>>
  disabled?: boolean
  limit?: number
  /**
   * Forwarded to /flightsearch via useBundleFlightData so calendar prices include
   * feederEstimatedPrice and share a Redux cache key with feeder-aware callers.
   */
  feederOriginAirportCode?: string
}

interface MaldivesDirectCalendarPricingResult {
  calendarPricingByDate: Record<string, CalendarItemBundleData>
  hotelPriceByCabinAndDate: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  isLoading: boolean
}

/**
 * Provides calendar pricing data from a non-stream (light) Maldives offer source.
 * This keeps calendar pricing decoupled from streamed tile offers.
 */
export function useMaldivesDirectCalendarPricing(
  params: UseMaldivesDirectCalendarPricingParams,
): MaldivesDirectCalendarPricingResult {
  const {
    rooms,
    fareClass,
    selectedFareClass,
    nights,
    charterBundleRoute,
    dateRangesByMonth,
    disabled = false,
    limit = HOTEL_OFFERS_LIMIT,
    feederOriginAirportCode,
  } = params

  // Fetch the offer list WITHOUT a cabin filter so the per-cabin hotel aggregate downstream
  // sees the full charter offer set. Server-filtering by `fareClass` here would drop offers
  // that are restricted to a different cabin (e.g. Business-only hotels) and make the
  // sibling cabin's column show stale prices when the page-level cabin toggles.
  // Per-cabin filtering is reapplied inside `useMaldivesOffersPricing` against
  // each offer's `campaignTags`.
  const { offerList, offers, offersFetching } = useMaldivesDirectLightOfferList({
    rooms,
    disabled,
    limit,
  })

  const { calendarPricingByDate, hotelPriceByCabinAndDate, isLoading: isOffersPricingLoading } = useMaldivesOffersPricing({
    offers,
    rooms,
    dateRangesByMonth,
    selectedFareClass,
    searchFareClass: fareClass,
    charterBundleRoute,
    nights,
    feederOriginAirportCode,
  })

  const isLoading = useMemo(
    () => offerList.fetching || offersFetching || isOffersPricingLoading,
    [offerList.fetching, offersFetching, isOffersPricingLoading],
  )
  const calendarPricingByDateWithLoading = useMemo<Record<string, CalendarItemBundleData>>(() => {
    const allCheckInDates = Object.values(dateRangesByMonth).flat().map(dateRange => dateRange.checkIn)
    if (!disabled && allCheckInDates.length === 0) {
      return calendarPricingByDate
    }

    const result: Record<string, CalendarItemBundleData> = disabled ? {} : { ...calendarPricingByDate }
    const defaultFetching = disabled || isLoading

    // Ensure every visible check-in date has an entry while pricing data is still loading.
    for (const checkIn of allCheckInDates) {
      const existingEntry = result[checkIn]
      if (!existingEntry) {
        result[checkIn] = {
          fetching: defaultFetching,
          available: false,
          totalPrice: 0,
          pricePerPerson: 0,
          offerCount: 0,
        }
        continue
      }

      // While aggregate pricing is loading, keep unresolved entries in loading state
      // to avoid flashing "Unavailable" before data is fully ready.
      if (defaultFetching && !existingEntry.available && existingEntry.offerCount === 0) {
        result[checkIn] = {
          ...existingEntry,
          fetching: true,
        }
      }
    }

    return result
  }, [disabled, dateRangesByMonth, calendarPricingByDate, isLoading])

  return {
    calendarPricingByDate: calendarPricingByDateWithLoading,
    hotelPriceByCabinAndDate,
    isLoading,
  }
}
